import {useStylesECDS} from '../ecds/ecdsStyle';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {user} from '../../../../store/reducers/user';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import Formatter from '../../../../util/formatter';
import uuidv4 from 'uuid/v4';
import useOpenState from '../../../../hooks/useOpenState';
import _, {cloneDeep} from 'lodash';
import moment from 'moment/moment';
import config from '../../../../app/config';
import {UtilsMomentJS} from '../../../../store/utilsMoment';
import Notes from '../../../../util/notes';
import {PATIENT_AGE_TYPE} from '../../../../util/patientContextUtil';
import Validator from '../../../../util/validator';
import InformationBox from '../../../../components/other/informationBox';
import {AgyleTable, createAgyleTableColumn} from '../../../../components/table/AgyleTable';
import {Divider, FormLabel, Grid} from '@mui/material';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import AgyleBlockSwitch from '../../../../components/ui/switch/agyleBlockSwitch';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import {calculateArrivalDate} from '../../../../util/patientUtil';
import Injury from '../ecds/injury';
import MessageBox from '../../../../util/messageBox';

const initCwiltedData = {
    cwilted: {
        escort: [],
        incident: '',
        witnessed: null,
        details: '',
        demeanour: '',
        incidentDate: null,
        incidentTime: null,
        incidentDateTime: null,
        incidentLocation: null,
        incidentLocationName: '',
    },
};

const InjuryCwilted = (props) => {
    const classes = useStylesECDS();
    const {t} = useTranslation();
    const userObject = useSelector(user);

    const firstUpdate = useRef(true);

    const tester = Formatter.formatUserFullName(userObject);

    // Table
    const [table, setTable] = useState([]);
    const [escort, setEscort] = useState({
        id: uuidv4() + '',
        parentalResponsibility: false,
        attendedWithChild: false,
        name: '',
        age: '',
        relationship: '',
        contact: '',
    });

    //  For Delete
    const [deleteItem, setDeleteItem] = useState(null);
    const [isConfirmDeleteOpen, openConfirmDelete, closeConfirmDelete] = useOpenState(false, true);

    // Validation
    const [error, setError] = useState(false);
    const errorPicker = useRef({
        incidentTime: null,
        incidentDate: null,
        incidentDateTime: null,
        injuryDate: null,
        injuryTime: null,
        injuryDateTime: null,
    });

    // Form data
    const [injury, setInjury] = useState(_.cloneDeep(initCwiltedData));
    const [drugsAlcoholInvolved, setDrugsAlcoholInvolved] = useState(false);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let data = _.cloneDeep(initCwiltedData);
        if (!_.isNil(props.selectedForm)) {
            data = {...props.selectedForm};
            if (!_.isEmpty(props.selectedForm?.cwilted?.escort)) {
                setTable([...props.selectedForm.cwilted.escort]);
            }

            if (!_.isEmpty(data.injuryAlcoholOrDrug)) {
                setDrugsAlcoholInvolved(true);
            }
        }

        if (props.patient?.triage || props.patient?.clerking || props.patient?.studies?.seeAndTreat) {
            const historicPresentingComplaint = props?.histPresComplaint ? props?.histPresComplaint : props.patient?.clerking?.historicPresentingComplaint
                ? props.patient.clerking?.historicPresentingComplaint
                : props.patient.triage?.historicPresentingComplaint
                    ? props.patient.triage?.historicPresentingComplaint
                    : props.patient?.studies?.seeAndTreat?.historyOfPresentingComplaint;

            data = {
                ...data,
                cwilted: {
                    ...data?.cwilted,
                    incident: data && data?.cwilted?.incident !== '' ? data?.cwilted?.incident : (historicPresentingComplaint || ''),
                },
            };

        }

        if (data?.cwilted?.incidentDate && data?.cwilted?.incidentTime) {
            let dateUTC;

            if (data?.cwilted?.incidentDateTime) {
                dateUTC = moment(data?.cwilted?.incidentDateTime);
            } else if (moment(data?.cwilted?.incidentDate, config.defaultDateFormat).isValid()) {
                dateUTC = moment.utc(`${moment(data?.cwilted?.incidentDate, config.defaultDateFormat).format(config.ecdsDateFormat)} ${data?.cwilted?.incidentTime}`).local();
            } else {
                dateUTC = UtilsMomentJS.getLocalDateFromUTCECDSFormat(data?.cwilted?.incidentDate, data?.cwilted?.incidentTime);
            }
            data = {
                ...data,
                cwilted: {
                    ...data.cwilted,
                    incidentDate: dateUTC.format(config.ecdsDateFormat),
                    incidentTime: dateUTC.format(config.ecdsTimeFormat),
                },
            };
        }

        if (!_.isEmpty(props.patient?.metadata?.escort) && _.isEmpty(data?.cwilted?.escort)) {
            const escort = _.cloneDeep(props.patient?.metadata?.escort);
            data['cwilted']['escort'] = escort;
            setTable(escort);
        }

        setInjury(data);
        setLoaded(true);
    }, [props.selectedForm]);

    const prepareNote = () => {
        return Notes.getInjuryNote(injury, props.patientAgeType, props.parentForm, props.isInjury, drugsAlcoholInvolved);
    };

    useEffect(() => {
        if (props.parentForm === 'Triage' || props.parentForm === 'SeeAndTreat') {
            if (props.isInjury !== '1') {
                props.errorPicker(errorPicker.current.incidentDateTime !== null);
            } else {
                props.errorPicker(errorPicker.current.injuryDate !== null || errorPicker.current.injuryTime !== null);
            }
        } else {
            if (props.patientAgeType !== PATIENT_AGE_TYPE.PAEDS) {
                props.errorPicker(errorPicker.current.injuryDate !== null || errorPicker.current.injuryTime !== null);
            } else {
                props.errorPicker(errorPicker.current.injuryDate !== null || errorPicker.current.injuryTime !== null
                    || errorPicker.current.incidentDateTime !== null);
            }
        }
    }, [errorPicker.current]);

    // save data
    useEffect(() => {
        if (!firstUpdate.current) {
            const id = uuidv4() + '';
            let data = cloneDeep(injury);
            const dateOfForm = moment().toISOString();
            const dateUTC = UtilsMomentJS.getUTCDateFromECDSFormat(data?.cwilted?.incidentDate, data?.cwilted?.incidentTime);

            data = {
                ...data,
                cwilted: {
                    ...data.cwilted,
                    incidentDate: (data.cwilted?.incidentDate && data.cwilted?.incidentTime) ? dateUTC.format(config.ecdsDateFormat) : null,
                    incidentTime: (data.cwilted?.incidentDate && data.cwilted?.incidentTime) ? dateUTC.format(config.ecdsTimeFormat) : null,
                },
            };

            const dataToSave = [{
                name: 'studies.injury',
                value: {
                    ...data,
                    id: id,
                    date: dateOfForm,
                    by: tester,
                    login: userObject?.userName,
                    parentForm: props.parentForm,
                    ageAtComplete: moment().diff(props.patient.patient.dob, 'years'),
                },
                noteToAddWithRefId: prepareNote(),
                referenceId: id,
            }];
            props.save(dataToSave);
        }
    }, [injury]);

    useEffect(() => {
        if (!firstUpdate.current) {
            setInjury(prevState => ({...prevState, cwilted: {...prevState?.cwilted, escort: table}}));
        }
    }, [table]);

    const validation = (min, max, data) => {
        return Validator.validEcdsFormLenght(min, max, data);
    };

    const handleChangeChild = (data) => {
        const injury = {
            injuryDate: data.InjuryDate,
            injuryTime: data.InjuryTime,
            injuryDateTime: data.InjuryDateTime,
            placeOfInjury: data.EmergencyCarePlaceOfInjury_SnomedCt,
            injuryIntent: data.EmergencyCareInjuryIntent_SnomedCt,
            injuryActivityStatus: data.EmergencyCareInjuryActivityStatus_SnomedCt,
            injuryActivityType: data.EmergencyCareInjuryActivityType_SnomedCt,
            injuryMechanism: data.EmergencyCareInjuryMechanism_SnomedCt,
            injuryAlcoholOrDrug: data.EmergencyCareInjuryAlcoholOrDrugInvolvement_SnomedCt,
            assaultLocationDescription: data.AssaultLocationDescription,
        };

        firstUpdate.current = false;
        setInjury(prevState => ({...prevState, ...injury}));
    };

    const handleChange = (event, type) => {
        const {name, value} = event.target;
        firstUpdate.current = false;
        // eslint-disable-next-line default-case
        switch (type) {
            case 'form': {
                setInjury(prevState => ({...prevState, cwilted: {...prevState?.cwilted, [name]: value}}));
                break;
            }
            case 'escort': {
                setEscort(prevState => ({...prevState, [name]: value}));
                break;
            }
        }
    };

    const handleContact = (e) => {
        const onlyNums = e.target.value.replace(/[^\d\-]/g, '');

        setEscort(prevState => ({...prevState, contact: onlyNums}));
    };

    const checkboxHandler = (checked, name, type) => {
        firstUpdate.current = false;
        // eslint-disable-next-line default-case
        switch (type) {
            case 'form': {
                setInjury(prevState => ({...prevState, cwilted: {...prevState?.cwilted, [name]: checked}}));
                break;
            }
            case 'escort': {
                setEscort(prevState => ({...prevState, [name]: checked}));
                break;
            }
        }
    };

    const addToTable = () => {
        const valid = escort.attendedWithChild ? escort.name !== '' && escort.relationship !== '' : escort.name !== '' && escort.relationship !== '' && escort.contact !== '';
        if (!valid) {
            setError(true);
        } else {
            firstUpdate.current = false;
            setTable(prevState => [...prevState, escort]);

            // Clear fields after add to table
            setEscort({
                id: uuidv4() + '',
                parentalResponsibility: false,
                attendedWithChild: false,
                name: '',
                age: '',
                relationship: '',
                contact: '',
            });
            setError(false);
        }
    };

    const renderInfo = useMemo(() => {
        const parents = _.find(injury?.cwilted?.escort, {parentalResponsibility: true});
        const attended = _.find(injury?.cwilted?.escort, {attendedWithChild: true});
        let message = 'sss';

        if (!parents && !attended) {
            message = 'Please add who does have parental/legal responsibility for the patient and what are their contact details, and who attended with a child';
        } else if (!parents && attended) {
            message = 'Please add who does have parental/legal responsibility for the patient and what are their contact details';
        } else if (parents && !attended) {
            message = 'Please add who attended with a child';
        } else {
            return;
        }

        return (
            <InformationBox type="info" style={{margin: '5px 0'}}>
                {t(message)}
            </InformationBox>
        );
    }, [injury?.cwilted?.escort]);

    const deleteReferral = () => {
        firstUpdate.current = false;
        setTable(_.filter(table, escort => {
            return escort.id !== deleteItem.id;
        }));
        setDeleteItem(null);
        closeConfirmDelete();
    };

    const handleDeleteRecord = (row) => {
        setDeleteItem(row);
        openConfirmDelete();
    };

    const renderTable = () => {
        const columns = [
            createAgyleTableColumn('no', 'No.', 1, null, 'count', 'notSort', 'medium', 'center', true, false, null, null, null),
            createAgyleTableColumn('ptName', 'Person Name', 2, 'name', 'String', '', 'medium', 'center', true, false, null, null, null),
            createAgyleTableColumn('responsibility', 'Parental Responsibility', 3, 'parentalResponsibility', 'boolean', '', 'medium', 'left', true, false, null, null, null),
            createAgyleTableColumn('attendedWithChild', 'Attended With a Child', 4, 'attendedWithChild', 'boolean', '', 'medium', 'left', true, true, null, null, null),
            createAgyleTableColumn('age', 'Age', 5, 'age', 'string', '', 'medium', 'left', true, false, null, null, null),
            createAgyleTableColumn('relationship', 'Relationship', 6, 'relationship', 'string', '', 'medium', 'left', true, false, null, null, null),
            createAgyleTableColumn('contact', 'Contact Details', 7, 'contact', 'string', '', 'medium', 'left', true, false, null, null, null),
            createAgyleTableColumn('delete', 'Action', 8, null, 'action', '', 'medium', 'right', true, false, null, null, null),
        ];

        if (injury?.cwilted?.escort) {
            return (
                <div style={{marginBottom: 10}}>
                    <AgyleTable
                        columns={columns}
                        data={injury?.cwilted?.escort}
                        deleteItem={handleDeleteRecord}
                        disableFilter
                    />
                </div>
            );
        }
    };

    const handleData = (name, event) => {
        firstUpdate.current = false;
        if (moment(event).isValid()) {
            setInjury(prevState => ({
                ...prevState,
                cwilted: {
                    ...prevState?.cwilted,
                    incidentTime: moment(event).format(config.ecdsTimeFormat),
                    incidentDate: moment(event).format(config.ecdsDateFormat),
                    incidentDateTime: moment(event).toISOString(),
                },
            }));

            errorPicker.current = {
                ...errorPicker.current,
                [name]: null,
            };
        } else {
            setInjury(prevState => ({
                ...prevState,
                cwilted: {...prevState?.cwilted, [name]: null},
            }));
            errorPicker.current = {
                ...errorPicker.current,
                [name]: 'Invalid',
            };
        }
    };

    const checkAge = () => {
        if (!_.isNil(props.selectedForm)) {
            return props.selectedForm?.ageAtComplete < 18;
        } else {
            return props.patientAgeType === PATIENT_AGE_TYPE.PAEDS;
        }
    };

    const renderCWILTED = () => {
        if (checkAge() && loaded) {
            return (
                <>
                    <Grid item xs={12}>
                        <AgyleTextField
                            name="incident"
                            label={t('Concern and Incident (History of Presenting Complaint)')}
                            onChange={event => handleChange(event, 'form')}
                            value={injury?.cwilted?.incident || ''}
                            debounce
                            fullWidth
                            noBorder={false}
                            withNewBackground
                            size="large"
                        />
                    </Grid>

                    <Grid container spacing={2} direction="row" style={{marginTop: 1}}>
                        <Grid item xs={12} md={3} className={classes.divRow}>
                            <AgyleBlockSwitch
                                value={injury?.cwilted?.witnessed || false}
                                onChange={event => checkboxHandler(event, 'witnessed', 'form')}
                            />

                            <FormLabel sx={{ml: 1}}>
                                {t('Witnessed')}
                            </FormLabel>
                        </Grid>

                        <Grid item xs={12} md={9} style={{paddingTop: 15}}>
                            <AgyleTextField
                                name="details"
                                label={t('Details')}
                                onChange={event => handleChange(event, 'form')}
                                value={injury?.cwilted?.details || ''}
                                debounce
                                fullWidth
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>
                    </Grid>

                    <Grid container direction="column" style={{margin: '5px 0'}}>
                        <div style={{width: '100%'}}>
                            {renderInfo}
                            {!_.isEmpty(injury?.cwilted?.escort) && renderTable()}
                        </div>
                    </Grid>

                    <Grid container spacing={2} direction="row" justifyContent="flex-start" alignItems="center">
                        <Grid item xs={12} md={3}>
                            <div className={classes.divRow}>
                                <AgyleBlockSwitch
                                    value={escort.parentalResponsibility}
                                    onChange={event => checkboxHandler(event, 'parentalResponsibility', 'escort')}
                                />

                                <FormLabel sx={{ml: 1}}>
                                    {t('Parental Responsibility')}
                                </FormLabel>
                            </div>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <AgyleTextField
                                name="name"
                                label={t('Name')}
                                onChange={event => handleChange(event, 'escort')}
                                value={escort.name}
                                error={error}
                                debounce
                                fullWidth
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>

                        <Grid item xs={12} md={1}>
                            <AgyleTextField
                                name="age"
                                label={t('Age')}
                                type="number"
                                onChange={event => handleChange(event, 'escort')}
                                value={escort.age}
                                maxLength={3}
                                debounce
                                fullWidth
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <AgyleTextField
                                name="relationship"
                                label={t('Relationship')}
                                onChange={event => handleChange(event, 'escort')}
                                value={escort.relationship}
                                error={error}
                                debounce
                                fullWidth
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <div className={classes.divRow}>
                                <AgyleBlockSwitch
                                    value={escort.attendedWithChild}
                                    onChange={event => checkboxHandler(event, 'attendedWithChild', 'escort')}
                                />

                                <FormLabel sx={{ml: 1}}>
                                    {t('Attended with a child')}
                                </FormLabel>
                            </div>
                        </Grid>

                        <Grid item xs={12} md={9}>
                            <AgyleTextField
                                name="contact"
                                label={t('Contact details')}
                                onChange={handleContact}
                                value={escort.contact}
                                error={(error && !escort.attendedWithChild)}
                                debounce
                                fullWidth
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>

                        <div style={{marginLeft: 'auto', marginTop: 10}}>
                            <AgyleButton
                                variant="contained"
                                onClick={addToTable}
                                startIcon={<PersonAddIcon/>}
                            >
                                {t('Add')}
                            </AgyleButton>
                        </div>
                    </Grid>

                    <Divider sx={{m: 1}}/>

                    <Grid item xs={12} style={{paddingTop: '10px'}}>
                        <AgyleTextField
                            name="demeanour"
                            label={t('Demeanour')}
                            onChange={event => handleChange(event, 'form')}
                            value={injury?.cwilted?.demeanour || ''}
                            debounce
                            fullWidth
                            noBorder={false}
                            withNewBackground
                            size="large"
                        />
                    </Grid>

                    {props.isInjury !== '1' && (
                        <Grid container spacing={2} direction="row" style={{marginTop: 1}}>
                            <Grid item xs={12}>
                                <AgyleDatePicker
                                    type="dateTime"
                                    label={t('Illness Date and Time')}
                                    value={injury?.cwilted?.incidentDateTime}
                                    onChange={(e) => handleData('incidentDateTime', e)}
                                    maxDate={config.injuryDateAfterAdmit ? moment() : moment(calculateArrivalDate(props.patient))}
                                    onError={event => {
                                        errorPicker.current = {
                                            ...errorPicker.current,
                                            incidentDateTime: event,
                                        };
                                    }}
                                    error={!injury?.cwilted?.incidentDateTime}
                                    noBorder={false}
                                    withNewBackground
                                    size="large"
                                />
                            </Grid>

                            <Grid item xs={12} style={{paddingTop: '10px'}}>
                                <AgyleTextField
                                    name="incidentLocationName"
                                    label={t('Location Name')}
                                    onChange={event => handleChange(event, 'form')}
                                    value={injury?.cwilted?.incidentLocationName || ''}
                                    debounce
                                    fullWidth
                                    noBorder={false}
                                    withNewBackground
                                    size="large"
                                />
                            </Grid>
                        </Grid>
                    )}
                </>
            );
        }
    };

    const handleError = (event, name) => {
        errorPicker.current = {
            ...errorPicker.current,
            [name]: event,
        };
    };

    return (
        <div className={classes.root}>
            <div style={{paddingBottom: 10}}>
                {renderCWILTED()}
            </div>
            {(props.isInjury === '1' || (props.parentForm !== 'Triage' && props.parentForm !== 'SeeAndTreat')) &&
                <>
                    {checkAge() && <Divider sx={{m: 1}}/>}

                    <Injury
                        setData={handleChangeChild}
                        noCode={true}
                        style={classes}
                        selectedForm={props.selectedForm}
                        patient={props.patient}
                        errorPicker={handleError}
                        checkLenght={validation}
                        drugsAlcoholInvolved={setDrugsAlcoholInvolved}
                        drugsAlcoholInvolvedValue={drugsAlcoholInvolved}
                    />
                </>
            }

            <MessageBox
                onClose={closeConfirmDelete}
                title={t('Confirmation')}
                message={t('Do you really want to remove this item?')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={deleteReferral}
                open={isConfirmDeleteOpen}
            />
        </div>
    );
};

export default InjuryCwilted;